import { Injectable, inject } from '@angular/core';
import { Observable, of, filter, map } from 'rxjs';

import { NavigationMenuItemDto } from '@common/data-services/navigation/models/navigation-menu-item.dto';
import { environment } from '@environment';

import { SidenavService } from '../../sidenav/sidenav.service';

/**
 * MFE codes that have no navigation menu item and are resolved directly from
 * environment.mfeUrls instead.
 */
const DIRECT_ACCESS_MFE_CODES: readonly string[] = ['engagements', 'reportsHub'];

export interface AppLoaderMfeConfig {
  uri: string;
  title?: string;
  fromNavigation: boolean;
  navigationDescription?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppLoaderMfeConfigService {
  private readonly sidenavService = inject(SidenavService);
  private cache = new Map<string, AppLoaderMfeConfig | null>();

  /**
   * Resolves the MFE configuration for a given code.
   * First checks navigation data, then falls back to environment.mfeUrls.
   */
  resolveMfeConfig(code: string | null): Observable<AppLoaderMfeConfig | null> {
    if (!code) {
      return of(null);
    }
    
    // Return cached result if available
    const cached = this.cache.get(code);
    if (cached !== undefined) {
      return of(cached);
    }

    return this.sidenavService.getNavigationData().pipe(
      filter(data => !!data),
      map(navigationData => {
        const navigationItem = this.findNavigationItemByCode(navigationData.NavigationItems, code);

        let config: AppLoaderMfeConfig | null = null;

        if (navigationItem) {
          const description = navigationItem.Description ?? '';
          config = {
            uri: environment.mfeUrls[description] ?? description,
            title: navigationItem.Value,
            fromNavigation: true,
            navigationDescription: description
          };
        } else if (DIRECT_ACCESS_MFE_CODES.includes(code)) {
          // Fallback to environment.mfeUrls for direct access MFEs only
          const directUri = environment.mfeUrls[code];
          if (directUri) {
            config = {
              uri: directUri,
              fromNavigation: false
            };
          }
        }

        this.cache.set(code, config);
        return config;
      })
    );
  }

  private findNavigationItemByCode(items: NavigationMenuItemDto[], code: string): NavigationMenuItemDto | null {
    for (const item of items) {
      // Only match items that are app-loader routes (State starts with /next/app/)
      const isAppLoaderRoute = item.State?.startsWith('/next/app/');
      if (isAppLoaderRoute && (item.Code === code || item.State === `/next/app/${code}`)) {
        return item;
      }

      if (item.HasChildren && item.Children?.length) {
        const found = this.findNavigationItemByCode(item.Children, code);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }
}
